import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

export function FinalCta() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const { language } = useLanguage();
  const t = translations[language].finalCta;

  return (
    <section className="py-24 px-6">
      <motion.div
        ref={sectionRef}
        className="max-w-3xl mx-auto text-center"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-5xl mb-4 leading-[1.15]"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <motion.p
          className="text-lg md:text-xl mb-10"
          style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
          variants={itemVariants}
        >
          {t.subheading}
        </motion.p>

        <motion.a
          href="#kontakt"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base"
          style={{
            background:
              "linear-gradient(135deg, var(--gold) 0%, var(--gold-bright) 50%, var(--gold) 100%)",
            backgroundSize: "200% 100%",
            color: "#0A0A0B",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            boxShadow: "0 0 40px var(--glow-gold-strong), 0 4px 24px rgba(0,0,0,0.4)",
            textDecoration: "none",
          }}
          variants={itemVariants}
          whileHover={{
            scale: 1.02,
            boxShadow: "0 0 60px var(--glow-gold-strong), 0 8px 32px rgba(0,0,0,0.5)",
          }}
          whileTap={{ scale: 0.98 }}
        >
          {t.cta}
          <ArrowRight className="w-4 h-4" strokeWidth={2} />
        </motion.a>
      </motion.div>
    </section>
  );
}
